/**
 * Command Score - VS Code-style fuzzy matching algorithm
 *
 * @module commandScore
 *
 * ## Source Attribution
 *
 * This algorithm was extracted from **cmdk v1.1.1** (https://github.com/pacocoursey/cmdk)
 * and is used under the MIT License. The original cmdk library is MIT licensed, making
 * this extraction permissible with proper attribution.
 *
 * ### Original Location
 *
 * The algorithm was extracted from the minified source at `node_modules/cmdk/dist/index.js`.
 * In the original minified code, the functions had the following names:
 * - `te` → unminified as `commandScore` (this function)
 * - `Re` → the wrapper function (handles keyword concatenation and case normalization)
 * - `Ce` → the default filter export
 *
 * ### Extraction Rationale
 *
 * **Why extracted**: cmdk v1.1.1 requires React 18+ due to its use of `useSyncExternalStore`.
 * This project uses React 17.0.2, which caused the runtime error:
 * ```
 * useSyncExternalStore is not a function
 * ```
 * This error occurred in Storybook and prevented the Command component from functioning.
 * By extracting just the scoring algorithm (which has no React dependencies), we can use
 * the VS Code-style fuzzy matching without the React 18+ requirement.
 *
 * ## Algorithm Description
 *
 * This implements VS Code-style fuzzy matching with position-weighted scoring - the same
 * algorithm used in VS Code's command palette (Cmd+Shift+P). It provides intelligent
 * fuzzy matching with the following scoring rules:
 *
 * - **Matches at string start** score highest (1.0)
 * - **Matches after word boundaries** (space/dash) score 0.9
 * - **Matches after separators** (/, _, ., etc.) score 0.8
 * - **Matches mid-word** score 0.17
 * - **Distance penalties** applied for gaps between matched characters
 * - **Case mismatch penalties** slightly reduce scores for case differences
 *
 * The algorithm uses recursive exploration with memoization to find the optimal match
 * path through the string, considering all possible ways the search term could match.
 */

// Score constants
const SCORE_CONTINUE_MATCH = 1;
const SCORE_WORD_BOUNDARY = 0.9;
const SCORE_SEPARATOR = 0.8;
const SCORE_MID_WORD = 0.17;
const SCORE_MIN = 0.1;
const PENALTY_SKIPPED = 0.999;
const PENALTY_CASE_MISMATCH = 0.9999;
const SCORE_MATCH_END = 0.99;

// Patterns for detecting match positions
const SEPARATORS = /[\\\/_+.#"@\[\(\{&]/;
const SEPARATORS_GLOBAL = /[\\\/_+.#"@\[\(\{&]/g;
const WORD_BOUNDARIES = /[\s-]/;
const WORD_BOUNDARIES_GLOBAL = /[\s-]/g;

type MemoCache = Record<string, number>;

/**
 * Core recursive scoring function with memoization
 */
function scoreRecursive(original: string, originalSearch: string, lowerOriginal: string, lowerSearch: string, stringIndex: number, searchIndex: number, memo: MemoCache): number {
  // If we've matched all search characters
  if (searchIndex === originalSearch.length) {
    // Perfect match if we've also consumed all original characters
    return stringIndex === original.length ? SCORE_CONTINUE_MATCH : SCORE_MATCH_END;
  }

  // Memoization key
  const key = `${stringIndex},${searchIndex}`;
  if (memo[key] !== undefined) {
    return memo[key];
  }

  // Current search character to find
  const searchChar = lowerSearch.charAt(searchIndex);

  // Find all positions where this character occurs
  let position = lowerOriginal.indexOf(searchChar, stringIndex);
  let bestScore = 0;

  while (position >= 0) {
    // Recursively score the rest of the match
    let score = scoreRecursive(original, originalSearch, lowerOriginal, lowerSearch, position + 1, searchIndex + 1, memo);

    if (score > bestScore) {
      // Score based on match position
      if (position === stringIndex) {
        // Match at current position (continuing a sequence)
        score *= SCORE_CONTINUE_MATCH;
      } else if (SEPARATORS.test(original.charAt(position - 1))) {
        // Match after a separator character
        score *= SCORE_SEPARATOR;

        // Penalty for skipped separators
        const skippedSeparators = original.slice(stringIndex, position - 1).match(SEPARATORS_GLOBAL);
        if (skippedSeparators && stringIndex > 0) {
          score *= Math.pow(PENALTY_SKIPPED, skippedSeparators.length);
        }
      } else if (WORD_BOUNDARIES.test(original.charAt(position - 1))) {
        // Match after a word boundary (space or dash)
        score *= SCORE_WORD_BOUNDARY;

        // Penalty for skipped word boundaries
        const skippedBoundaries = original.slice(stringIndex, position - 1).match(WORD_BOUNDARIES_GLOBAL);
        if (skippedBoundaries && stringIndex > 0) {
          score *= Math.pow(PENALTY_SKIPPED, skippedBoundaries.length);
        }
      } else {
        // Match mid-word
        score *= SCORE_MID_WORD;

        // Penalty for distance from expected position
        if (stringIndex > 0) {
          score *= Math.pow(PENALTY_SKIPPED, position - stringIndex);
        }
      }

      // Case mismatch penalty
      if (original.charAt(position) !== originalSearch.charAt(searchIndex)) {
        score *= PENALTY_CASE_MISMATCH;
      }
    }

    // Optimization: try skipping current character if it leads to a better match
    if (score < SCORE_MIN && lowerOriginal.charAt(position - 1) === lowerSearch.charAt(searchIndex + 1)) {
      const skipScore = scoreRecursive(original, originalSearch, lowerOriginal, lowerSearch, position + 1, searchIndex + 2, memo);
      if (skipScore * SCORE_MIN > score) {
        score = skipScore * SCORE_MIN;
      }
    }

    // Handle repeated characters optimization
    if (lowerSearch.charAt(searchIndex + 1) === lowerSearch.charAt(searchIndex) && lowerOriginal.charAt(position - 1) !== lowerSearch.charAt(searchIndex)) {
      const repeatScore = scoreRecursive(original, originalSearch, lowerOriginal, lowerSearch, position + 1, searchIndex + 2, memo);
      if (repeatScore * SCORE_MIN > score) {
        score = repeatScore * SCORE_MIN;
      }
    }

    if (score > bestScore) {
      bestScore = score;
    }

    // Try next occurrence of this character
    position = lowerOriginal.indexOf(searchChar, position + 1);
  }

  memo[key] = bestScore;
  return bestScore;
}

/**
 * Normalize a string for comparison
 * Converts to lowercase and normalizes whitespace
 */
function normalizeString(str: string): string {
  return str.toLowerCase().replace(WORD_BOUNDARIES_GLOBAL, ' ');
}

/**
 * Calculate a fuzzy match score between a value and search term.
 *
 * @param value - The string to search within
 * @param search - The search term to match
 * @param keywords - Optional additional keywords to include in matching
 * @returns A score from 0 to 1, where higher is a better match
 *
 * @example
 * ```ts
 * commandScore('Calendar', 'cal') // Returns high score (~0.9)
 * commandScore('Calendar', 'car') // Returns lower score
 * commandScore('New Calendar Event', 'nce') // Matches word boundaries
 * ```
 */
export function commandScore(value: string, search: string, keywords?: ReadonlyArray<string>): number {
  // Include keywords in the searchable string
  const searchableValue = keywords && keywords.length > 0 ? `${value} ${keywords.join(' ')}` : value;

  // Early return for empty search
  if (!search) {
    return 1;
  }

  // Early return for empty value
  if (!searchableValue) {
    return 0;
  }

  return scoreRecursive(searchableValue, search, normalizeString(searchableValue), normalizeString(search), 0, 0, {});
}

/**
 * Default filter function for Command component.
 * Returns the score if it's above a minimum threshold, otherwise 0.
 */
export function defaultFilter(value: string, search: string, keywords?: ReadonlyArray<string>): number {
  return commandScore(value, search, keywords);
}
